// import { useCallback, useState, useEffect, useContext } from "react";
// import { AppContext } from "../../../Context/AppContext";
// import { API_RECORDS_COUNT, SORT_ORDER } from "../../../Constants/config";
// import { apiGetRequest } from "../../../Helpers/Requests";
// import endpoints from "../../../Helpers/Endpoints";

// const useJobsData = () => {
//   const { profileData } = useContext(AppContext);

//   const [isPendingJobsLoading, setIsPendingJobsLoading] = useState(false);
//   const [isJobsLoading, setIsJobsLoading] = useState(false);
//   const [pendingRequestsJobsData, setPendingRequestsJobsData] = useState([]);
//   const [activeJobsData, setActiveJobsData] = useState([]);
//   const [completedJobsData, setCompletedJobsData] = useState([]);
//   const [missedJobsData, setMissedJobsData] = useState([]);

//   const getJobsData = useCallback(
//     async (type, options = {}) => {
//       try {
//         const requestParams = {
//           type,
//           profileId: profileData.id,
//           pageNumber: 1,
//           pageSize: API_RECORDS_COUNT,
//           sortOrder: SORT_ORDER.DESC,
//           ...options,
//         };

//         const response = await apiGetRequest(
//           endpoints.getSupplierJobsData(requestParams),
//         );

//         return response.data;
//       } catch (error) {
//         console.error(`Error fetching ${type} jobs:`, error);
//         return [];
//       }
//     },
//     [profileData],
//   );

//   const loadAllJobsData = useCallback(async () => {
//     setIsPendingJobsLoading(true);
//     setIsJobsLoading(true);
//     const [pending, active, completed, missed] = await Promise.all([
//       getJobsData("pending-requests"),
//       getJobsData("active", { sortBy: "due_date" }),
//       getJobsData("completed", { sortBy: "completed_date" }),
//       getJobsData("missed"),
//     ]);

//     setPendingRequestsJobsData(pending);
//     setActiveJobsData(active);
//     setCompletedJobsData(completed);
//     setMissedJobsData(missed);

//     setIsPendingJobsLoading(false);
//     setIsJobsLoading(false);
//   }, [getJobsData]);

//   useEffect(() => {
//     loadAllJobsData();
//   }, [loadAllJobsData]);

//   return {
//     pendingRequestsJobsData,
//     activeJobsData,
//     completedJobsData,
//     missedJobsData,

//     isPendingJobsLoading,
//     isJobsLoading,
//   };
// };

// export default useJobsData;

import { useCallback, useState, useContext, useEffect, useMemo } from "react";
import { AppContext } from "../../../Context/AppContext";
import { useSocketContext } from "../../../Context/SocketContext/SocketProvider";
import { API_RECORDS_COUNT, SORT_ORDER } from "../../../Constants/config";
import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";

const useJobsData = () => {
  const { profileData } = useContext(AppContext);
  const { socket } = useSocketContext();

  const [pendingRequestsJobsData, setPendingRequestsJobsData] = useState([]);
  const [activeJobsData, setActiveJobsData] = useState([]);
  const [completedJobsData, setCompletedJobsData] = useState([]);
  const [missedJobsData, setMissedJobsData] = useState([]);

  const [isPendingJobsLoading, setIsPendingJobsLoading] = useState(false);
  const [isJobsLoading, setIsJobsLoading] = useState(false);
  // Store + restaurant totals per list. The service lists above only count
  // service jobs, but the status-tab counts are for every order type.
  const [catalogTotals, setCatalogTotals] = useState({
    active: 0,
    completed: 0,
    missed: 0,
  });

  const getJobsData = useCallback(
    async (type, options = {}) => {
      const requestParams = {
        type,
        profileId: profileData.id,
        pageNumber: 1,
        pageSize: API_RECORDS_COUNT,
        sortOrder: SORT_ORDER.DESC,
        ...options,
      };


      const response = await apiGetRequest(
        endpoints.getSupplierJobsData(requestParams),
      );

      return response.data;
    },
    [profileData],
  );

  // One row per vertical is enough — only `total_count` is read. Coupon orders
  // are store/restaurant orders, so they're already in these totals.
  const fetchCatalogTotal = useCallback(
    async (listType) => {
      try {
        const results = await Promise.all(
          ["product", "restaurant"].map((itemType) =>
            getJobsData(listType, { itemType, pageSize: 1 }),
          ),
        );
        const total = results.reduce((sum, r) => sum + (r?.total_count || 0), 0);
        setCatalogTotals((prev) => ({ ...prev, [listType]: total }));
      } catch (err) {
        console.error(`Failed to count ${listType} catalog jobs:`, err);
      }
    },
    [getJobsData],
  );

  const fetchPendingJobs = useCallback(
    async (isMobile = false) => {
      isMobile ? setIsJobsLoading(true) : setIsPendingJobsLoading(true);
      try {
        const data = await getJobsData("pending-requests", {
          sortBy: "creation_date",
        });
        setPendingRequestsJobsData(data);
      } finally {
        isMobile ? setIsJobsLoading(false) : setIsPendingJobsLoading(false);
      }
    },
    [getJobsData],
  );

  const fetchActiveJobs = useCallback(async () => {
    setIsJobsLoading(true);
    fetchCatalogTotal("active");
    try {
      const data = await getJobsData("active", { sortBy: "due_date" });
      setActiveJobsData(data);
    } finally {
      setIsJobsLoading(false);
    }
  }, [getJobsData, fetchCatalogTotal]);

  const fetchCompletedJobs = useCallback(async () => {
    setIsJobsLoading(true);
    fetchCatalogTotal("completed");
    try {
      const data = await getJobsData("completed", {
        sortBy: "completed_date",
      });
      setCompletedJobsData(data);
    } finally {
      setIsJobsLoading(false);
    }
  }, [getJobsData, fetchCatalogTotal]);

  const fetchMissedJobs = useCallback(async () => {
    setIsJobsLoading(true);
    fetchCatalogTotal("missed");
    try {
      const data = await getJobsData("missed", {
        sortBy: "creation_date",
      });
      setMissedJobsData(data);
    } finally {
      setIsJobsLoading(false);
    }
  }, [getJobsData, fetchCatalogTotal]);

  // Generic fetch for the Active Jobs section parameterized by item type
  // (service vs product) — used by the item-type tabs. Returns the list
  // response (does not store it; the caller owns that state).
  const fetchJobsList = useCallback(
    async (jobsType, itemType, pageNumber = 1) => {
      const sortBy =
        jobsType === "completed"
          ? "completed_date"
          : jobsType === "active"
            ? "due_date"
            : "creation_date";
      return getJobsData(jobsType, { sortBy, itemType, pageNumber });
    },
    [getJobsData],
  );

  // ---- Pagination -------------------------------------------------------
  // Every list endpoint is paginated and reports `max_page_number`; the UI used
  // to request page 1 only and ignore that field, so anything past the first
  // 50 rows was simply unreachable. Each list keeps the whole response object
  // (the container reads `?.data`) and `loadMoreFor` appends the next page onto
  // it, carrying the fresh page/total metadata forward.
  const LIST_SETTERS = useMemo(
    () => ({
      pending: [pendingRequestsJobsData, setPendingRequestsJobsData, "pending-requests", "creation_date"],
      active: [activeJobsData, setActiveJobsData, "active", "due_date"],
      completed: [completedJobsData, setCompletedJobsData, "completed", "completed_date"],
      missed: [missedJobsData, setMissedJobsData, "missed", "creation_date"],
    }),
    [pendingRequestsJobsData, activeJobsData, completedJobsData, missedJobsData],
  );

  const hasMoreFor = useCallback(
    (listKey) => {
      const entry = LIST_SETTERS[listKey];
      if (!entry) return false;
      const current = entry[0];
      return (current?.page || 1) < (current?.max_page_number || 1);
    },
    [LIST_SETTERS],
  );

  const loadMoreFor = useCallback(
    async (listKey) => {
      const entry = LIST_SETTERS[listKey];
      if (!entry) return;
      const [current, setter, type, sortBy] = entry;
      const nextPage = (current?.page || 1) + 1;
      if (nextPage > (current?.max_page_number || 1)) return;

      const next = await getJobsData(type, { sortBy, pageNumber: nextPage });
      setter((prev) => ({
        ...next,
        data: [...(prev?.data || []), ...(next?.data || [])],
      }));
    },
    [LIST_SETTERS, getJobsData],
  );

  const loadAllJobsData = useCallback(async () => {
    if (!profileData?.id) return;

    setIsPendingJobsLoading(true);
    setIsJobsLoading(true);
    ["active", "completed", "missed"].forEach(fetchCatalogTotal);

    try {
      const [pending, active, completed, missed] = await Promise.all([
        getJobsData("pending-requests", { sortBy: "creation_date" }),
        getJobsData("active", { sortBy: "due_date" }),
        getJobsData("completed", { sortBy: "completed_date" }),
        getJobsData("missed", { sortBy: "creation_date" }),
      ]);

      setPendingRequestsJobsData(pending);
      setActiveJobsData(active);
      setCompletedJobsData(completed);
      setMissedJobsData(missed);
    } catch (err) {
      console.error("Initial load failed", err);
    } finally {
      setIsPendingJobsLoading(false);
      setIsJobsLoading(false);
    }
  }, [getJobsData, fetchCatalogTotal, profileData?.id]);

  useEffect(() => {
    loadAllJobsData();
  }, [loadAllJobsData]);

  // Listen for new pending requests via socket (replaces polling)
  useEffect(() => {
    if (!socket || !profileData?.id) return;

    const handleNewPendingRequest = async () => {
      try {
        const data = await getJobsData("pending-requests", {
          sortBy: "creation_date",
        });
        setPendingRequestsJobsData(data);
      } catch (err) {
        console.error("Error refreshing pending jobs:", err);
      }
    };

    socket.on("new_supplier_pending_request", handleNewPendingRequest);

    return () => {
      socket.off("new_supplier_pending_request", handleNewPendingRequest);
    };
  }, [socket, getJobsData, profileData?.id]);

  return {
    pendingRequestsJobsData,
    activeJobsData,
    completedJobsData,
    missedJobsData,

    isPendingJobsLoading,
    isJobsLoading,

    fetchPendingJobs,
    fetchActiveJobs,
    fetchCompletedJobs,
    fetchMissedJobs,
    fetchJobsList,

    catalogTotals,

    hasMoreFor,
    loadMoreFor,
  };
};

export default useJobsData;
